import { Formik, FormikProps } from 'formik';
import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { stepExportFulfilled } from '../../../../actions/registerShipmentActions';
import { IFormSelectOption } from '../../../../interfaces/forms/forms';
import IPageViewModel from '../../../../interfaces/umbracoNodes/pageViewModel';
import { IStore } from '../../../../reducers';
import { IRegisterShipmentReducer } from '../../../../reducers/registerShipmentReducer';
import { A } from '../../../base/typography/anchors';
import { WizardPageProps } from '../../../wizard/wizard';
import { buildStepExportSchema, IStepExportFormInfo, stepExportInitialValues } from './stepExport.schema';
import * as StyledStepExport from './stepExport.styles';
import StepExportForm from './stepExportForm';
import { documentsContentLine, DOCUMENTS_HS_CODE, ShipmentReturnPolicy } from './stepExportForm/helpers';

interface Props extends WizardPageProps {
	name: string;
	page: IPageViewModel;
}

const StepExport: FC<Props> = ({ onPrevious, onSubmit, page }) => {
	const dispatch = useDispatch<any>();
	const { t } = useTranslation('registerShipment');
	const registerShipmentReducer: IRegisterShipmentReducer = useSelector<IStore, IRegisterShipmentReducer>(
		store => store.registerShipmentReducer
	);
	const { stepExportFormInfo } = registerShipmentReducer;
	const { stepInfoFormInfo, stepShipmentFormInfo } = useSelector(
		({ registerShipmentReducer }) => registerShipmentReducer
	);
	const { country } = stepInfoFormInfo;
	const isInternationalLetter = stepShipmentFormInfo?.serviceOption === 'InternationalLetter';
	const isUsaOrPr = country?.value === 'US' || country?.value === 'PR';
	const usdCurrency: IFormSelectOption = { value: 'USD', label: 'USD' };
	const iskCurrency: IFormSelectOption = { value: 'ISK', label: 'ISK' };
	const formikRef = useRef<FormikProps<IStepExportFormInfo>>(null);

	// USA/PR shipments with package type 'D' (Documents) also ship the hardcoded documents line,
	// just like international letters. Detect that from the persisted package type.
	const isUsaOrPrDocuments = isUsaOrPr && stepExportFormInfo.packageType?.value === 'D';
	const forcedDocuments = isInternationalLetter || isUsaOrPrDocuments;

	// When the user goes back and switches away from a documents shipment, the persisted contents
	// may still be just the hardcoded documents line — clear it so the parcel form starts empty
	// rather than carrying the documents item over.
	const contentsIsOnlyHardcodedDocuments =
		stepExportFormInfo.contents?.length === 1 && stepExportFormInfo.contents[0]?.hsTariffCode === DOCUMENTS_HS_CODE;
	const nonDocumentsContents = contentsIsOnlyHardcodedDocuments
		? stepExportInitialValues.contents
		: stepExportFormInfo.contents;
	// When leaving a documents shipment that only had the hardcoded line, also clear the forced type
	// and currency so the parcel form starts fresh.
	const nonDocumentsPackageType = contentsIsOnlyHardcodedDocuments
		? stepExportInitialValues.packageType
		: stepExportFormInfo.packageType;
	const nonDocumentsCurrency = contentsIsOnlyHardcodedDocuments
		? stepExportInitialValues.currency
		: stepExportFormInfo.currency;

	const initialExportFormValues: IStepExportFormInfo = {
		...stepExportFormInfo,
		currency: isUsaOrPr ? usdCurrency : isInternationalLetter ? iskCurrency : nonDocumentsCurrency,
		// International letters force the Documents package type; USA/PR keep the user's chosen type.
		packageType: isInternationalLetter ? { value: 'D', label: t('step-export.documents') } : nonDocumentsPackageType,
		returnPolicy: isInternationalLetter
			? { value: ShipmentReturnPolicy.ReturnToSender, label: t('step-export.return') }
			: stepExportFormInfo.returnPolicy,
		contents: forcedDocuments ? [{ ...documentsContentLine }] : nonDocumentsContents,
	};

	useEffect(() => {
		if (formikRef.current) {
			formikRef.current.resetForm({ values: initialExportFormValues });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepShipmentFormInfo.serviceOption]);

	const onStepExportSubmit = (values: IStepExportFormInfo) => {
		// If destination is USA or PR, force currency to USD and hide price if package type is 'D' (Documents)
		const shouldHidePrice = isUsaOrPr && values.packageType?.value === 'D';
		const basePayload = {
			...values,
			currency: isUsaOrPr ? usdCurrency : values.currency,
		};
		const payload = shouldHidePrice
			? {
					...basePayload,
					contents: values.contents.map(contentLine => ({
						...contentLine,
						price: '0',
					})),
			  }
			: basePayload;

		dispatch(stepExportFulfilled(payload));
		onSubmit(payload);
	};

	return (
		<StyledStepExport.Wrapper>
			<StyledStepExport.Header>{t('step-export.header')}</StyledStepExport.Header>
			<StyledStepExport.Intro>
				{!isUsaOrPr && (
					<>
						{t('step-export.more-info')}
						<A href={page.Values.TermsLink.Url} target={page.Values.TermsLink.Target}>
							{` (${page.Values.TermsLink.Name})`}
						</A>
					</>
				)}
			</StyledStepExport.Intro>
			{isUsaOrPr && <StyledStepExport.USAMessage>{t('step-export.messageUSA')}</StyledStepExport.USAMessage>}
			<Formik
				initialValues={initialExportFormValues}
				enableReinitialize
				innerRef={formikRef}
				validationSchema={buildStepExportSchema(country?.value, isInternationalLetter)}
				onSubmit={onStepExportSubmit}>
				{({ values, setFieldTouched, setFieldValue, isValid, isSubmitting, errors }) => (
					<StepExportForm
						values={values}
						errors={errors}
						setFieldTouched={setFieldTouched}
						setFieldValue={setFieldValue}
						onPrevious={onPrevious}
						isSubmitting={isSubmitting}
						page={page}
						isValid={isValid}
					/>
				)}
			</Formik>
		</StyledStepExport.Wrapper>
	);
};

export default StepExport;
